import { useMemo, useState } from "react";
import "./BriefingCard.css";

/**
 * Briefing Card — an at-a-glance daily surface: Schedule (time tiles) + inline
 * Tasks + Watchlist, in one card. Presentational + pure: all data comes in via
 * props; the only stateful bits (active-tile, task sort, due chips) are derived
 * from `now`. See docs spec "XAVIOR — Briefing Card Spec".
 */

export interface BriefingEvent {
  id: string;
  title: string;
  /** ISO datetime, or null/undefined for an all-day event. */
  start?: string | null;
  end?: string | null;
  allDay?: boolean;
  /** Attendee display names (initials are derived). */
  attendees?: string[];
  /** Recurrence frequency, if any. Annual (birthdays/anniversaries) is kept in
   *  the curated view; weekly/daily/monthly (standups) are hidden. */
  recurringFreq?: "yearly" | "monthly" | "weekly" | "daily" | "other";
}

export interface BriefingTask {
  id: string;
  title: string;
  /** ISO date (YYYY-MM-DD or datetime); undefined = no due date. */
  due?: string | null;
  done?: boolean;
}

export type BriefingWatchItem =
  | {
      kind: "ticker";
      id: string;
      name: string;
      symbol?: string;
      /** Pre-formatted price string, e.g. "$63,079". Omit until a quote feed exists. */
      price?: string;
      /** Percent change; sign drives green/red. Omit if no quote. */
      deltaPct?: number;
      /** Price-history points for the sparkline (optional, additive). */
      spark?: number[];
    }
  | {
      kind: "news";
      id: string;
      headline: string;
      developing?: boolean;
      /** e.g. "2h ago". */
      recency?: string;
    };

export interface BriefingCardProps {
  events: BriefingEvent[];
  tasks: BriefingTask[];
  watchlist: BriefingWatchItem[];
  /** Injected for deterministic tests; defaults to the current time. */
  now?: Date;
  onToggleTask?: (id: string) => void;
  onOpenCalendar?: () => void;
  /** Click an event row/tile → open it in the calendar (id + start date). */
  onOpenEvent?: (id: string, start?: string | null) => void;
  onEditWatchlist?: () => void;
  onRefresh?: () => void;
  emptyDayNote?: string;
  /** Cap the task list so the briefing stays at-a-glance (default 6). */
  maxTasks?: number;
  /** Tab the card opens on (0=today; 10/30/90-day curated views). */
  initialHorizon?: BriefingHorizon;
}

/** Schedule horizon: Today, or the next N days (curated to key events). */
export type BriefingHorizon = 0 | 10 | 30 | 90;
const HORIZONS: { key: BriefingHorizon; label: string }[] = [
  { key: 0, label: "Today" },
  { key: 10, label: "10 days" },
  { key: 30, label: "30 days" },
  { key: 90, label: "90 days" },
];
const HORIZON_LABEL: Record<BriefingHorizon, string> = {
  0: "today",
  10: "next 10 days",
  30: "next 30 days",
  90: "next 90 days",
};
/** Cap the curated horizon list — show only the N most important events. */
const MAX_KEY_EVENTS = 10;
/** Rough importance score to pick the top events when there are more than the cap. */
function eventImportance(e: BriefingEvent): number {
  if (isTravelEvent(e)) return 100;
  if (isDeadlineEvent(e)) return 90;
  const att = e.attendees?.length ?? 0;
  if (att > 0) return 50 + Math.min(att, 10);
  if (isBirthdayEvent(e)) return 40;
  return 20;
}
const TRAVEL_RE = /\b(fly|flight|flying|flew|travel|trip|vacation|depart|arrive|arrival|airport|hotel|check-?in|drive to|train to)\b/i;
const DEADLINE_RE = /\b(deadline|due|close|closing|filing|file|submit|deliver|launch|ship|review|board|quarterly|renew|expire|expires|payment|invoice|sign|contract)\b/i;
const BIRTHDAY_RE = /\b(birthday|b-?day|anniversary)\b/i;
const isTravelEvent = (e: BriefingEvent) => TRAVEL_RE.test(e.title);
const isDeadlineEvent = (e: BriefingEvent) => DEADLINE_RE.test(e.title);
const isBirthdayEvent = (e: BriefingEvent) => BIRTHDAY_RE.test(e.title);
/** Heavy curation for the horizon view: hide recurring series (weekly standups)
 *  and all-day holidays — but keep every real one-off: timed events (meetings,
 *  appointments like an angiogram), travel, deadlines, and all-day
 *  birthdays/anniversaries. */
const isKeyEvent = (e: BriefingEvent) => {
  if (isBirthdayEvent(e)) return true; // always keep birthdays/anniversaries
  // Sub-annual recurring (weekly WSU, daily standups) is routine → hidden.
  // Annual recurring is fine.
  if (e.recurringFreq && e.recurringFreq !== "yearly") return false;
  // All-day: keep a multi-day trip, deadline, or annual marker; hide holidays.
  if (e.allDay) return isTravelEvent(e) || isDeadlineEvent(e) || e.recurringFreq === "yearly";
  return true; // timed one-offs (meetings, appointments)
};

type CtxKind = "people" | "travel" | "deadline" | "birthday" | "time";
function eventContext(e: BriefingEvent): { kind: CtxKind; text: string } {
  if (isBirthdayEvent(e)) return { kind: "birthday", text: "birthday" };
  const att = e.attendees ?? [];
  if (att.length === 1) return { kind: "people", text: `with ${att[0].split(/\s+/)[0]}` };
  if (att.length > 1) return { kind: "people", text: `${att.length} people` };
  if (isTravelEvent(e)) return { kind: "travel", text: "travel" };
  if (isDeadlineEvent(e)) return { kind: "deadline", text: "deadline" };
  return { kind: "time", text: fmtRange(e.start, e.end) || (e.allDay ? "all day" : "") };
}
function monthDay(iso?: string | null): { weekday: string; mon: string; day: string } {
  if (!iso) return { weekday: "", mon: "", day: "" };
  const d = new Date(iso);
  return {
    weekday: d.toLocaleString("en-US", { weekday: "short" }),
    mon: d.toLocaleString("en-US", { month: "short" }),
    day: String(d.getDate()),
  };
}

const ACCENT = "#15a5e3";
const MAX_AVATARS = 2;

// ── helpers ──────────────────────────────────────────────────────────────────
const ms = (s?: string | null) => (s ? new Date(s).getTime() : NaN);

function fmtTime(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  let h = d.getHours();
  const m = d.getMinutes();
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")}`;
}

function fmtRange(start?: string | null, end?: string | null): string {
  if (!start) return "";
  const sp = new Date(start).getHours() >= 12 ? "PM" : "AM";
  if (!end) return `${fmtTime(start)} ${sp}`;
  const ep = new Date(end).getHours() >= 12 ? "PM" : "AM";
  // Per-end meridiem so a range crossing noon isn't mislabeled (11:30 AM – 1:00 PM).
  return sp === ep
    ? `${fmtTime(start)} – ${fmtTime(end)} ${ep}`
    : `${fmtTime(start)} ${sp} – ${fmtTime(end)} ${ep}`;
}

function fmtDuration(start?: string | null, end?: string | null): string {
  const a = ms(start);
  const b = ms(end);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return "";
  const mins = Math.round((b - a) / 60000);
  if (mins % 60 === 0) return `${mins / 60} hr`;
  return `${mins} min`;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
};

/** Parse a due value to a local Date. Date-only strings (YYYY-MM-DD) parse as
 *  UTC by spec, which shifts a day in negative-offset zones — pin them to local. */
function parseDue(due: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(due);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(due);
}

/** Overdue (<0), today (0), or upcoming (>0) in whole days; NaN if no due date. */
function dueDays(due: string | null | undefined, now: Date): number {
  if (!due) return NaN;
  return Math.round((startOfDay(parseDue(due)) - startOfDay(now)) / 86400000);
}

function dueChip(days: number, due: string | null | undefined): { cls: string; label: string } | null {
  if (Number.isNaN(days)) return null;
  if (days < 0) {
    const n = -days;
    return { cls: "bc-chip-overdue", label: `${n} day${n === 1 ? "" : "s"} late` };
  }
  if (days === 0) return { cls: "bc-chip-today", label: "Today" };
  return {
    cls: "bc-chip-upcoming",
    label: parseDue(due!).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
  };
}

function sparkPath(spark: number[], w = 56, h = 20): string {
  if (spark.length < 2) return "";
  const min = Math.min(...spark);
  const max = Math.max(...spark);
  const span = max - min || 1;
  const stepX = w / (spark.length - 1);
  return spark
    .map((v, i) => {
      const x = i * stepX;
      const y = h - ((v - min) / span) * h;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

// ── icons (inline to match the spec exactly; app uses heroicons/sidekick) ─────
const stroke = { fill: "none", stroke: ACCENT, strokeWidth: 1.7, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
const CalendarIcon = () => (
  <svg className="bc-hd-icon" viewBox="0 0 24 24" width="15" height="15" {...stroke}>
    <rect x="3" y="4.5" width="18" height="16" rx="2.5" /><path d="M3 9h18M8 2.5v4M16 2.5v4" />
  </svg>
);
const CheckboxIcon = () => (
  <svg className="bc-hd-icon" viewBox="0 0 24 24" width="15" height="15" {...stroke}>
    <rect x="3.5" y="3.5" width="17" height="17" rx="3" /><path d="M8 12l3 3 5-6" />
  </svg>
);
const ChartIcon = () => (
  <svg className="bc-hd-icon" viewBox="0 0 24 24" width="15" height="15" {...stroke}>
    <path d="M3 16l5-5 4 3 6-7" /><path d="M14 7h4v4" />
  </svg>
);

function SectionHeader({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="bc-header">
      {icon}
      <span>{label}</span>
    </div>
  );
}

const ctxStroke = { fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
function CtxIcon({ kind }: { kind: CtxKind }) {
  if (kind === "people")
    return (
      <svg className="bc-ctx-icon" viewBox="0 0 24 24" width="13" height="13" {...ctxStroke}>
        <circle cx="9" cy="8" r="3" /><path d="M3.5 19a5.5 5.5 0 0 1 11 0M16 7.5a3 3 0 0 1 0 6M16.5 19a5.5 5.5 0 0 0-2-4.3" />
      </svg>
    );
  if (kind === "travel")
    return (
      <svg className="bc-ctx-icon" viewBox="0 0 24 24" width="13" height="13" {...ctxStroke}>
        <path d="M21 15.5l-7.5-2.5V6a1.5 1.5 0 0 0-3 0v7L3 15.5v2l7.5-2v3L8 20.2V22l3.9-1.1L16 22v-1.8L13.5 18.5v-3l7.5 2z" />
      </svg>
    );
  if (kind === "deadline")
    return (
      <svg className="bc-ctx-icon" viewBox="0 0 24 24" width="13" height="13" {...ctxStroke}>
        <path d="M6 21V4M6 4h11l-2 3.5 2 3.5H6" />
      </svg>
    );
  if (kind === "birthday")
    return (
      <svg className="bc-ctx-icon" viewBox="0 0 24 24" width="13" height="13" {...ctxStroke}>
        <path d="M4 20h16M5 20v-7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v7M8 11V8M12 11V8M16 11V8M12 8V5" />
      </svg>
    );
  return null;
}

/**
 * The canonical "event stack" — date column (weekday/day/month) + title +
 * context rows. Used by the horizon views here and by the chat schedule card
 * (EventsResultCard), so every schedule surface shares one visual language.
 */
export function EventStack({
  events,
  onOpenEvent,
}: {
  events: BriefingEvent[];
  onOpenEvent?: (id: string, start?: string | null) => void;
}) {
  return (
    <div className="bc-events bc-events-multiday">
      {events.map((e) => {
        const { weekday, mon, day } = monthDay(e.start);
        const ctx = eventContext(e);
        return (
          <button key={e.id} type="button" className="bc-event-row" onClick={onOpenEvent ? () => onOpenEvent(e.id, e.start) : undefined}>
            <span className="bc-event-date">
              <span className="bc-event-wd">{weekday}</span>
              <span className="bc-event-day">{day}</span>
              <span className="bc-event-mon">{mon}</span>
            </span>
            <span className="bc-event-main">
              <span className="bc-event-title">{e.title}</span>
              <span className="bc-event-ctx">
                <CtxIcon kind={ctx.kind} />
                {ctx.text}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Avatars({ names, variant }: { names: string[]; variant: "active" | "quiet" }) {
  const shown = names.slice(0, MAX_AVATARS);
  const overflow = names.length - shown.length;
  return (
    <div className={`bc-avatars bc-avatars-${variant}`}>
      {shown.map((n, i) => (
        <span key={i} className="bc-avatar">{initials(n)}</span>
      ))}
      {overflow > 0 && <span className="bc-avatar bc-avatar-more">+{overflow}</span>}
    </div>
  );
}

export function BriefingCard({
  events,
  tasks,
  watchlist,
  now = new Date(),
  onToggleTask,
  onOpenCalendar,
  onOpenEvent,
  onEditWatchlist,
  onRefresh,
  emptyDayNote = "Light day — nothing on the calendar.",
  maxTasks = 6,
  initialHorizon = 0,
}: BriefingCardProps) {
  const t = now.getTime();
  const [horizon, setHorizon] = useState<BriefingHorizon>(initialHorizon);
  const isToday = horizon === 0;

  // End of the active window: end of today, or +N days for a horizon.
  const windowEndMs = useMemo(() => {
    const d = new Date(now);
    d.setHours(23, 59, 59, 999);
    if (horizon > 0) d.setDate(d.getDate() + horizon);
    return d.getTime();
  }, [now, horizon]);

  // Upcoming events in the window: not yet ended, and starting before window end.
  const windowEvents = useMemo(
    () =>
      events
        .filter((e) => {
          const endMs = ms(e.end ?? e.start);
          const startMs = ms(e.start);
          const notEnded = e.allDay || !Number.isFinite(endMs) || endMs > t;
          const inWindow = !Number.isFinite(startMs) || startMs <= windowEndMs;
          return notEnded && inWindow;
        })
        .sort((a, b) => ms(a.start) - ms(b.start)),
    [events, t, windowEndMs],
  );

  // Today shows everything remaining; multi-day curates to key events and caps
  // at the N most important (then re-sorts chronologically for display).
  const keyEvents = useMemo(() => {
    if (isToday) return windowEvents;
    const curated = windowEvents.filter(isKeyEvent);
    if (curated.length <= MAX_KEY_EVENTS) return curated;
    return [...curated]
      .sort((a, b) => eventImportance(b) - eventImportance(a))
      .slice(0, MAX_KEY_EVENTS)
      .sort((a, b) => ms(a.start) - ms(b.start));
  }, [windowEvents, isToday]);
  const hiddenCount = windowEvents.length - keyEvents.length;

  // Exactly one active tile (Today): the live event, else the next upcoming.
  const activeId = useMemo(() => {
    const timed = keyEvents.filter((e) => !e.allDay && e.start);
    const live = timed.find((e) => ms(e.start) <= t && t < (ms(e.end) || Infinity));
    if (live) return live.id;
    const upcoming = timed
      .filter((e) => ms(e.start) > t)
      .sort((a, b) => ms(a.start) - ms(b.start))[0];
    return upcoming?.id ?? null;
  }, [keyEvents, t]);

  const openTasks = useMemo(
    () =>
      tasks
        .filter((task) => !task.done)
        .map((task) => ({ ...task, _days: dueDays(task.due, now) }))
        .sort((a, b) => {
          // urgency bucket: overdue(0) → today(1) → upcoming/none(2), then by date
          const bucket = (d: number) => (Number.isNaN(d) ? 2 : d < 0 ? 0 : d === 0 ? 1 : 2);
          const ba = bucket(a._days);
          const bb = bucket(b._days);
          if (ba !== bb) return ba - bb;
          const da = Number.isNaN(a._days) ? Infinity : a._days;
          const db = Number.isNaN(b._days) ? Infinity : b._days;
          return da - db;
        }),
    [tasks, now],
  );

  const hasWatch = watchlist.length > 0;
  const eventWord = (n: number) => `${isToday ? "" : "key "}event${n === 1 ? "" : "s"}`;

  return (
    <div className="bc-card">
      {/* ── Top bar: title + horizon tabs ──────────────────────────── */}
      <div className="bc-topbar">
        <span className="bc-title">Today</span>
        <div className="bc-horizons">
          {HORIZONS.map((h) => (
            <button
              key={h.key}
              type="button"
              className={`bc-horizon${horizon === h.key ? " is-active" : ""}`}
              onClick={() => setHorizon(h.key)}
            >
              {h.label}
            </button>
          ))}
          {onRefresh && (
            <button type="button" className="bc-refresh-icon" onClick={onRefresh} aria-label="Refresh" title="Refresh">
              ↻
            </button>
          )}
        </div>
      </div>

      {/* ── Schedule ───────────────────────────────────────────────── */}
      <SectionHeader
        icon={<CalendarIcon />}
        label={`Schedule · ${HORIZON_LABEL[horizon]} · ${keyEvents.length} ${eventWord(keyEvents.length)}`}
      />
      {keyEvents.length === 0 ? (
        <div className="bc-empty-day">{isToday ? emptyDayNote : "Nothing notable in this window."}</div>
      ) : isToday ? (
        <div className="bc-events">
          {keyEvents.map((e) => {
            const active = e.id === activeId;
            const onClick = onOpenEvent ? () => onOpenEvent(e.id, e.start) : undefined;
            const clickCls = onOpenEvent ? " is-clickable" : "";
            if (e.allDay) {
              return (
                <div key={e.id} className={`bc-tile bc-tile-allday${active ? " is-active" : ""}${clickCls}`} onClick={onClick}>
                  <span className="bc-tile-title">{e.title}</span>
                  <span className="bc-chip-allday">All day</span>
                </div>
              );
            }
            const hasPeople = (e.attendees?.length ?? 0) > 0;
            return (
              <div key={e.id} className={`bc-tile${active ? " is-active" : ""}${clickCls}`} onClick={onClick}>
                <div className="bc-time">
                  <span className="bc-time-start">{fmtTime(e.start)}</span>
                  {fmtDuration(e.start, e.end) && <span className="bc-time-dur">{fmtDuration(e.start, e.end)}</span>}
                </div>
                <div className="bc-tile-divider" />
                <div className="bc-tile-body">
                  <span className="bc-tile-title">{e.title}</span>
                  {hasPeople ? (
                    <Avatars names={e.attendees!} variant={active ? "active" : "quiet"} />
                  ) : (
                    <span className="bc-tile-sub">{fmtRange(e.start, e.end)}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <EventStack events={keyEvents} onOpenEvent={onOpenEvent} />
      )}
      {!isToday && hiddenCount > 0 && (
        <button type="button" className="bc-link bc-hidden" onClick={onOpenCalendar}>
          {hiddenCount} routine event{hiddenCount === 1 ? "" : "s"} hidden <span aria-hidden>→</span>
        </button>
      )}
      {onOpenCalendar && (
        <button type="button" className="bc-link bc-link-cal" onClick={onOpenCalendar}>
          Open calendar <span aria-hidden>→</span>
        </button>
      )}

      {/* ── Tasks (Today only — horizon views are events-only) ─────── */}
      {isToday && openTasks.length > 0 && (
        <div className="bc-section">
          <SectionHeader icon={<CheckboxIcon />} label={`Tasks · ${openTasks.length}`} />
          <div className="bc-tasks">
            {openTasks.slice(0, maxTasks).map((task) => {
              const chip = dueChip(task._days, task.due);
              return (
                <div key={task.id} className="bc-task">
                  <button
                    type="button"
                    className="bc-check"
                    aria-label={`Complete ${task.title}`}
                    onClick={() => onToggleTask?.(task.id)}
                  />
                  <span className="bc-task-title">{task.title}</span>
                  {chip && <span className={`bc-chip ${chip.cls}`}>{chip.label}</span>}
                </div>
              );
            })}
            {openTasks.length > maxTasks && (
              <div className="bc-task-more">+{openTasks.length - maxTasks} more</div>
            )}
          </div>
        </div>
      )}

      {/* ── Watchlist (Today only) ─────────────────────────────────── */}
      {isToday && hasWatch && (
        <div className="bc-section">
          <SectionHeader icon={<ChartIcon />} label={`Watchlist · ${watchlist.length}`} />
          <div className="bc-watch">
            {watchlist.map((w) =>
              w.kind === "ticker" ? (
                <div key={w.id} className="bc-watch-row bc-ticker">
                  <div className="bc-ticker-name">
                    <span className="bc-ticker-title">{w.name}</span>
                    {w.symbol && <span className="bc-ticker-sym">{w.symbol}</span>}
                  </div>
                  {w.spark && w.spark.length > 1 && (
                    <svg className="bc-spark" width="56" height="20" viewBox="0 0 56 20" fill="none">
                      <path
                        d={sparkPath(w.spark)}
                        stroke={(w.deltaPct ?? 0) < 0 ? "var(--text-danger)" : "var(--text-success)"}
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                  <div className="bc-ticker-quote">
                    {w.price && <span className="bc-ticker-price">{w.price}</span>}
                    {typeof w.deltaPct === "number" && (
                      <span className={`bc-ticker-delta ${w.deltaPct < 0 ? "is-loss" : "is-gain"}`}>
                        {w.deltaPct >= 0 ? "+" : ""}
                        {w.deltaPct}%
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div key={w.id} className="bc-watch-row bc-news">
                  <span className={`bc-news-dot${w.developing ? " is-developing" : ""}`} />
                  <div className="bc-news-body">
                    <span className="bc-news-headline">{w.headline}</span>
                    <span className="bc-news-sub">News{w.developing ? " · developing" : ""}</span>
                  </div>
                  {w.recency && <span className="bc-news-recency">{w.recency}</span>}
                </div>
              ),
            )}
          </div>
          {onEditWatchlist && (
            <button type="button" className="bc-link bc-link-watch" onClick={onEditWatchlist}>
              Edit watchlist <span aria-hidden>→</span>
            </button>
          )}
        </div>
      )}

      {/* Watchlist link is the one thing that survives a fully-empty card. */}
      {isToday && !hasWatch && onEditWatchlist && (
        <div className="bc-section">
          <button type="button" className="bc-link bc-link-watch" onClick={onEditWatchlist}>
            Edit watchlist <span aria-hidden>→</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default BriefingCard;
